import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { mergeGasFamilyData } from './gas-calibration-profile';
import {
  certificateImageAsDataUrl,
  drawCertificateFooter,
  drawCertificateHeader,
  drawCertificateLegalText,
  drawReferenceStandardsTable,
} from './certificate-pdf-layout';

const LOGO_URL = '/logo-calins-color-hd.png';
const SIGNATURE_URL = new URL('../assets/firma-daniel-kemeny.png', import.meta.url).href;

function num(value) { const number = Number(value); return Number.isFinite(number) ? number : null; }
function fmt(value, decimals = 3) { const number = num(value); return number === null ? '—' : number.toFixed(decimals).replace('.', ','); }
function pdfSafe(value) {
  return String(value ?? '—')
    .replace(/₂/g, '2')
    .replace(/₃/g, '3')
    .replace(/√/g, 'raíz')
    .replace(/≤/g, '<=')
    .replace(/≥/g, '>=');
}
function functionalResult(value) {
  const key = String(value || '').toLowerCase();
  if (['satisfactory', 'pass', 'passed', 'ok'].includes(key)) return 'SATISFACTORIO';
  if (['unsatisfactory', 'fail', 'failed'].includes(key)) return 'NO SATISFACTORIO';
  if (['not_performed', 'not performed', 'pending', ''].includes(key)) return 'NO REALIZADA';
  return pdfSafe(value).toUpperCase();
}
function dmy(value) {
  if (!value) return '—';
  const text = String(value);
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(text) ? `${text}T12:00:00` : text);
  return Number.isNaN(date.getTime()) ? '—' : `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
}
function certificateNumber(...values) {
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (/^\d+$/.test(text)) return text;
    const match = text.match(/OS-(\d+)-(\d+)$/i);
    if (match) return `${match[1]}${match[2]}`;
  }
  return '—';
}
async function imageAsDataUrl(url) {
  return certificateImageAsDataUrl(url);
}
function header(doc, { logo, number, page, pages, first }) {
  drawCertificateHeader(doc, { logo, number, page, pages, first });
}
function footer(doc, page, pages) {
  drawCertificateFooter(doc, page, pages);
}
function labelValue(doc, label, value, x, y, valueX = x + 38) {
  doc.setFont('helvetica', 'bold'); doc.text(label, x, y); doc.setFont('helvetica', 'normal'); doc.text(pdfSafe(value || '—'), valueX, y);
}
function legal(doc, y) {
  return drawCertificateLegalText(doc, y);
}

function pageOne(doc, { session, instrument, results, familyData }) {
  let y = legal(doc, 66) + 3;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.text('ESPECIFICACIONES', 14, y); y += 8; doc.setFontSize(9.5);
  const left = [['Objeto:', session.instrument_object || instrument?.type], ['Fabricante:', session.instrument_brand || instrument?.brand], ['Modelo:', session.instrument_model || instrument?.model], ['N.° de serie:', session.instrument_serial || instrument?.serial], ['TAG:', session.instrument_tag || instrument?.tag]];
  const right = [['Determinación:', familyData.profile_mode === 'multigas' ? 'Detector multigás' : 'Detector monogás'], ['Fecha calibración:', dmy(session.calibration_date)], ['Fecha emisión:', dmy(session.report_issue_date)], ['Válido hasta:', dmy(session.valid_until)], ['Lugar:', session.instrument_location]];
  left.forEach(([label, value], index) => labelValue(doc, label, value, 14, y + (index * 5.5), 48));
  right.forEach(([label, value], index) => labelValue(doc, label, value, 108, y + (index * 5.5), 148));
  y += 37; doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.text('CLIENTE', 14, y); y += 8; doc.setFontSize(9.5);
  [['Razón social:', session.client_name], ['Dirección:', session.client_address], ['Ciudad:', session.client_city], ['Provincia:', session.client_province]].forEach(([label, value], index) => labelValue(doc, label, value, 26, y + (index * 5.5), 58));
  y += 30; doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.text('CONDICIONES AMBIENTALES', 14, y);
  autoTable(doc, { startY: y + 5, margin: { left: 28, right: 28 }, theme: 'grid', styles: { fontSize: 9, halign: 'center', cellPadding: 2 }, headStyles: { fillColor: [180, 215, 225], textColor: 0, fontStyle: 'bold' }, head: [['Temperatura', 'Presión', 'Humedad']], body: [[`${fmt(session.ambient_temp_avg, 1)} °C`, `${fmt(session.ambient_pressure_avg, 1)} hPa`, `${fmt(session.ambient_humidity_avg, 1)} %HR`]] });
  y = (doc.lastAutoTable?.finalY || y) + 10; doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.text('RESULTADOS', 14, y);
  const details = new Map((familyData.result_details || []).map((detail) => [Number(detail.point_no), detail]));
  autoTable(doc, {
    startY: y + 5, margin: { left: 10, right: 10 }, theme: 'grid', styles: { fontSize: 8, halign: 'center', cellPadding: 1.8 }, headStyles: { fillColor: [180, 215, 225], textColor: 0, fontStyle: 'bold' },
    head: [['Gas', 'Patrón', 'Detector', 'Desviación', 'Desv. %', 'U (k=2)', 'Tolerancia', 'Estado']],
    body: results.map((result) => { const detail = details.get(Number(result.point_no)) || {}; const unit = detail.unit || ''; return [pdfSafe(detail.analyte_label || '—'), `${fmt(result.pattern_mean)} ${unit}`, `${fmt(result.instrument_mean)} ${unit}`, fmt(result.error_abs), `${fmt(result.error_pct, 2)} %`, `${fmt(result.u_reported)} ${unit}`, `± ${fmt(detail.tolerance_abs)} ${unit}`, result.in_tolerance == null ? '—' : result.in_tolerance ? 'SATISFACTORIO' : 'FALLA']; }),
  });
}

function pageTwo(doc, { session, familyData, signature }) {
  const reference = familyData.standard_configuration?.reference || {};
  const asset = reference.asset || {}; const certificate = reference.certificate || {}; const budget = familyData.uncertainty_budget || {};
  let y = 42; doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text('MÉTODO Y TRAZABILIDAD', 14, y); y += 8; doc.setFont('helvetica', 'normal'); doc.setFontSize(9.5);
  doc.text(doc.splitTextToSize(familyData.method, 180), 14, y); y += 12;
  drawReferenceStandardsTable(doc, {
    startY: y,
    references: [{
      ...reference,
      usage: 'Gas patrón',
      serial: familyData.cylinder_number || asset.serial,
      scope: reference.capability?.function_label || 'Mezcla certificada',
    }],
  });
  y = (doc.lastAutoTable?.finalY || y) + 10; doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text('VERIFICACIONES FUNCIONALES', 14, y);
  autoTable(doc, { startY: y + 5, margin: { left: 14, right: 14 }, theme: 'grid', styles: { fontSize: 8.5, cellPadding: 2 }, headStyles: { fillColor: [180, 215, 225], textColor: 0, fontStyle: 'bold' }, head: [['Prueba de cero', 'Prueba de alarmas', 'Respuesta', 'Caudal']], body: [[functionalResult(familyData.zero_test_result), functionalResult(familyData.alarm_test_result), familyData.response_time_seconds ? `${familyData.response_time_seconds} s` : '—', `${fmt(familyData.regulator_flow_l_min, 2)} L/min`]] });
  y = (doc.lastAutoTable?.finalY || y) + 10; doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text('PRESUPUESTO DE INCERTIDUMBRE', 14, y);
  autoTable(doc, { startY: y + 5, margin: { left: 14, right: 14 }, theme: 'grid', styles: { fontSize: 8, cellPadding: 1.8 }, headStyles: { fillColor: [180, 215, 225], textColor: 0, fontStyle: 'bold' }, head: [['Contribución', 'Tratamiento', 'Dato']], body: [['Gas patrón certificado', 'U / k', `Cert. ${certificate.certificate_no || '—'} · k=${fmt(certificate.coverage_factor, 0)}`], ['Resolución del detector', 'd / raíz(12)', 'Por canal'], ['Repetibilidad', 's / raíz(n)', `${familyData.repeat_count} lectura(s)`], ['Caudal', 'Límite / raíz(3)', `${fmt(budget.flow_percent, 2)} %`], ['Temperatura', 'Límite / raíz(3)', `${fmt(budget.temperature_coefficient_percent_per_10c, 2)} % / 10 °C`], ['Otras', 'Límite / raíz(3)', `${fmt(budget.other_percent, 2)} %`]] });
  y = (doc.lastAutoTable?.finalY || y) + 10; doc.setFont('helvetica', 'normal'); doc.setFontSize(9.3);
  const rule = familyData.decision_rule === 'guard_band' ? 'Conformidad: |desviación| + U <= tolerancia.' : 'Conformidad: |desviación| <= tolerancia.';
  doc.text(rule, 14, y); y += 7; doc.setFont('helvetica', 'bold'); doc.text('OBSERVACIONES', 14, y); y += 6; doc.setFont('helvetica', 'normal'); doc.text(doc.splitTextToSize(session.notes || 'Sin observaciones.', 180), 14, y);
  if (signature) doc.addImage(signature, 'PNG', 78, 238, 44, 29, undefined, 'FAST');
}

export async function generateGasNoSacCertificatePdf({ session, instrument, results = [] }, options = {}) {
  if (!session) throw new Error('La sesión es requerida.');
  if (!results.length) throw new Error('No hay resultados para certificar.');
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const [logo, signature] = await Promise.all([imageAsDataUrl(options.logoUrl || LOGO_URL), imageAsDataUrl(options.signatureUrl || SIGNATURE_URL)]);
  const number = certificateNumber(options.serviceOrderCode, session.service_order_code, session.certificate_number, instrument?.lab_id);
  const familyData = mergeGasFamilyData(session.family_data);
  pageOne(doc, { session, instrument, results, familyData }); doc.addPage(); pageTwo(doc, { session, familyData, signature });
  const pages = doc.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) { doc.setPage(page); header(doc, { logo, number, page, pages, first: page === 1 }); footer(doc, page, pages); }
  if (options.returnBlob) return new Blob([doc.output('arraybuffer')], { type: 'application/pdf' });
  doc.save(options.filename || `${number !== '—' ? number : 'R-DT-PC-GAS'}.pdf`);
  return null;
}

